import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { resolveMediaUrl } from "@/lib/media-url";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Heart, 
  ChatCircle, 
  Trash, 
  Code,
  PaperPlaneTilt,
  Calendar
} from "@phosphor-icons/react";
import CodeSandbox from "@/components/CodeSandbox";

export default function PostDetailPage() {
  const { postId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [likeAnimating, setLikeAnimating] = useState(false);

  const fetchPost = useCallback(async () => {
    try {
      const response = await apiClient.get(`/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      toast.error("Post not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [navigate, postId]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await apiClient.get(`/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to load comments");
    }
  }, [postId]);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [fetchPost, fetchComments]);

  const handleLike = async () => {
    if (!post) return;
    setLikeAnimating(true);
    try {
      const response = await apiClient.post(`/posts/${postId}/like`);
      setPost({
        ...post,
        liked_by_user: response.data.liked,
        likes_count: response.data.likes_count
      });
    } catch (error) {
      toast.error("Failed to update like");
    }
    setTimeout(() => setLikeAnimating(false), 400);
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await apiClient.post(`/posts/${postId}/comments`, {
        content: newComment
      });
      setComments([response.data, ...comments]);
      setNewComment("");
      setPost(prev => prev ? { ...prev, comments_count: prev.comments_count + 1 } : null);
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await apiClient.delete(`/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      setPost(prev => prev ? { ...prev, comments_count: prev.comments_count - 1 } : null);
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleDeletePost = async () => {
    if (!confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await apiClient.delete(`/posts/${postId}`);
      toast.success("Post deleted");
      navigate("/");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const canDelete = post.author_id === user.id || ["admin", "teacher"].includes(user.role);
  const hasCode = post.html_code || post.css_code || post.js_code;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-surface transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold truncate flex-1 min-w-0">{post.title}</h1>
          {canDelete && (
            <button
              onClick={handleDeletePost}
              className="shrink-0 p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
              data-testid="delete-post-button"
            >
              <Trash className="w-5 h-5" />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <article className="bg-surface rounded-xl border border-border/40 overflow-hidden">
          {/* Author Info */}
          <div className="p-6 border-b border-border/40">
            <div className="flex items-center gap-3">
              <Link to={`/profile/${post.author_id}`}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-lg font-bold text-background">
                  {post.author_name?.charAt(0).toUpperCase()}
                </div>
              </Link>
              <div>
                <Link 
                  to={`/profile/${post.author_id}`}
                  className="font-semibold hover:text-primary transition-colors"
                  data-testid="post-author-link"
                >
                  {post.author_name}
                </Link>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div>
              <h2 className="font-heading text-2xl font-bold mb-3">{post.title}</h2>
              <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">
                {post.description}
              </p>
            </div>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Media */}
            {post.media_urls.length > 0 && (
              <div className="grid gap-4">
                {post.media_urls.map((url, idx) => {
                  const isVideo = url.match(/\.(mp4|webm|mov)$/i);
                  const isAudio = url.match(/\.(mp3|wav|ogg)$/i);
                  
                  if (isVideo) {
                    return (
                      <div key={idx} className="rounded-lg overflow-hidden bg-black/70 border border-border/30">
                        <video
                          src={resolveMediaUrl(url)}
                          controls
                          playsInline
                          preload="metadata"
                          className="w-full h-auto max-h-[65vh] object-contain bg-black"
                          data-testid={`post-video-${idx}`}
                        />
                      </div>
                    );
                  }
                  
                  if (isAudio) {
                    return (
                      <audio
                        key={idx}
                        src={resolveMediaUrl(url)}
                        controls
                        className="w-full"
                        data-testid={`post-audio-${idx}`}
                      />
                    );
                  }
                  
                  return (
                    <div key={idx} className="rounded-lg overflow-hidden bg-black/70 border border-border/30">
                      <img
                        src={resolveMediaUrl(url)}
                        alt={`Media ${idx + 1}`}
                        className="w-full h-auto max-h-[70vh] object-contain bg-black"
                        data-testid={`post-image-${idx}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Code Sandbox */}
            {hasCode && (
              <div className="tracing-beam rounded-xl overflow-hidden">
                <div className="bg-surface-elevated p-4 border-b border-border/40 flex items-center gap-2">
                  <Code className="w-5 h-5 text-accent" />
                  <span className="font-mono text-sm font-medium">Live Preview</span>
                </div>
                <CodeSandbox
                  html={post.html_code || ""}
                  css={post.css_code || ""}
                  js={post.js_code || ""}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 border-t border-border/40 flex items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                post.liked_by_user 
                  ? "bg-danger/10 text-danger" 
                  : "hover:bg-surface-elevated text-muted-foreground hover:text-foreground"
              }`}
              data-testid="like-button"
            >
              <Heart 
                weight={post.liked_by_user ? "fill" : "regular"} 
                className={`w-5 h-5 ${likeAnimating ? "heartbeat" : ""}`}
              />
              <span className="font-medium">{post.likes_count}</span>
            </button>
            <div className="flex items-center gap-2 text-muted-foreground">
              <ChatCircle className="w-5 h-5" />
              <span>{post.comments_count} comments</span>
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <section className="mt-6">
          <h3 className="font-heading font-semibold text-lg mb-4">Comments</h3>
          
          {/* Comment Form */}
          <form onSubmit={handleComment} className="mb-6">
            <div className="flex gap-3">
              <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-bold text-background">
                {user.full_name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="w-full h-12 pl-4 pr-12 bg-surface border border-border rounded-full focus:outline-none focus:border-primary transition-colors"
                  data-testid="comment-input"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="comment-submit"
                >
                  <PaperPlaneTilt weight="fill" className="w-5 h-5" />
                </button>
              </div>
            </div>
          </form>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              comments.map(comment => (
                <div 
                  key={comment.id} 
                  className="bg-surface rounded-xl p-4 border border-border/40"
                  data-testid={`comment-${comment.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Link to={`/profile/${comment.author_id}`}>
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/70 to-accent/70 flex items-center justify-center text-sm font-bold text-background">
                        {comment.author_name?.charAt(0).toUpperCase()}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link 
                          to={`/profile/${comment.author_id}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {comment.author_name}
                        </Link>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-foreground/90">{comment.content}</p>
                    </div>
                    {(comment.author_id === user.id || ["admin", "teacher"].includes(user.role)) && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="p-1 text-muted-foreground hover:text-danger transition-colors"
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
